// Schema of every editable field per page. Wrap words in *asterisks* for italics.
import { sixDomains, pillars, accessOptions, vehicleGrid, fundDetails } from './mock';

const VEHICLES = vehicleGrid.tiers.flatMap((tier) => ['growth', 'income'].map((p) => {
  const v = vehicleGrid.vehicles[tier][p];
  const d = fundDetails.find((f) => f.name === v.name);
  return { tier, purpose: p === 'growth' ? 'Growth' : 'Income', name: v.name, tag: v.tag, description: d ? d.description : v.description };
}));

const T = (key, label, def) => ({ key, label, type: 'text', default: def });
const A = (key, label, def) => ({ key, label, type: 'textarea', default: def });
const I = (key, label) => ({ key, label, type: 'image', default: '' });
const L = (key, label, itemFields, def) => ({ key, label, type: 'list', itemFields, default: def });
const f = (key, label, type = 'text') => ({ key, label, type });

export const PAGE_SCHEMA = {
  site: {
    label: 'Site-wide (header & footer)',
    sections: [
      { title: 'Header menu', fields: [
        T('nav.home', 'Menu: Home', 'Home'),
        T('nav.team', 'Menu: Team', 'Team'),
        T('nav.advisory', 'Menu: Advisory Services', 'Advisory Services'),
        T('nav.investment', 'Menu: Investment Solutions', 'Investment Solutions'),
        T('nav.contact', 'Menu: Contact button', 'Contact'),
      ] },
      { title: 'Footer', fields: [
        A('footer.tagline', 'Tagline', 'Leave no stone unturned. Leave no goal unmet.'),
        T('footer.sitemap_label', 'Sitemap heading', 'SITEMAP'),
        T('footer.offices_label', 'Offices heading', 'OFFICES'),
        L('footer.offices', 'Offices', [f('name', 'Office name'), f('address', 'Address')], [
          { name: 'New York', address: '535 Fifth Avenue, Suite 2400' },
          { name: 'Philadelphia', address: '1650 Market Street, Suite 3600' },
          { name: 'San Francisco', address: '505 Montgomery Street, Suite 1100' },
        ]),
        T('footer.copyright', 'Copyright (year is added automatically)', 'Stone Temple Partners LLC. All rights reserved.'),
        T('footer.contact_line', 'Contact line', 'thall@stpartners.co  ·  (917) 837-4617  ·  www.stpartners.co'),
        A('footer.disclaimer', 'Legal disclaimer', 'Stone Temple Partners LLC and its affiliates ("Stone Temple") provide integrated wealth advisory services. Information on this site is for general informational purposes only and does not constitute legal, tax, accounting, or investment advice. Investing involves risk. Past performance is not indicative of future results.'),
      ] },
    ],
  },
  home: {
    label: 'Home',
    sections: [
      { title: 'Hero', fields: [
        T('hero.kicker', 'Kicker', 'STONE TEMPLE PARTNERS · EST. 2020'),
        T('hero.title_line1', 'Heading line 1', 'Guiding'),
        T('hero.title_line2', 'Heading line 2 (italic)', 'Generational Wealth'),
        A('hero.body', 'Body paragraph', 'When your wealth reaches the point where you are moving on to the bigger questions in life, you have outgrown traditional wealth management. Stone Temple Partners combines deep institutional expertise with personalized guidance — partnering with you to guide your generational wealth so you can focus on what matters most.'),
      ] },
      { title: 'Horizontal founders photo', fields: [
        I('founders_photo.url', 'Image (upload or paste URL)'),
        T('founders_photo.caption_kicker', 'Overlay kicker', 'THE FOUNDERS'),
        T('founders_photo.caption', 'Overlay caption', 'Independent by design, aligned by choice.'),
      ] },
      { title: 'Pillar — Who We Are', fields: [
        T('pillar1.label', 'Label', 'Who We Are'),
        T('pillar1.subtitle', 'Subtitle', 'Built by practitioners'),
        A('pillar1.body', 'Body', "A team that has spent nearly a decade running dozens of sophisticated family offices — including our own. We don't bring a solution to clients until we understand their near-term objectives, long-term goals, and their values."),
      ] },
      { title: 'Pillar — Who We Serve', fields: [
        T('pillar2.label', 'Label', 'Who We Serve'),
        T('pillar2.subtitle', 'Subtitle', 'Families, advisors, and institutions'),
        A('pillar2.body', 'Body', 'Full Spectrum Advisory for private clients, RIA consulting, and purpose-built investment solutions — three coordinated ways to work with the families and advisors we serve.'),
      ] },
      { title: 'Pillar — How We Serve', fields: [
        T('pillar3.label', 'Label', 'How We Serve'),
        T('pillar3.subtitle', 'Subtitle', 'Every dollar has a purpose'),
        A('pillar3.body', 'Body', 'Most plans never ask what it is. Full Spectrum Advisory treats every financial decision as part of one coherent strategy — organized around six interconnected domains of wealth and expressed through purpose-built portfolios.'),
      ] },
      { title: 'Our Commitment', fields: [
        T('commitment.kicker', 'Kicker', 'OUR COMMITMENT'),
        T('commitment.subtitle', 'Subtitle', 'Partnership that endures across generations.'),
        A('commitment.body', 'Body', 'Stone Temple Partners was built by practitioners for the families we serve. We invest alongside you, align our incentives to yours, and bring the same institutional discipline to your balance sheet that we bring to our own — so every decision earns the right to be part of your long-term plan.'),
      ] },
    ],
  },
  team: {
    label: 'Team page',
    sections: [
      { title: 'Hero', fields: [
        T('hero.kicker', 'Kicker', 'OUR FIRM'),
        T('hero.title', 'Heading', 'Team.'),
        T('hero.subtitle', 'Subtitle', 'Built by practitioners, backed by our own capital.'),
        A('hero.body', 'Body paragraph', "Stone Temple Partners was founded by a team that has spent nearly a decade running dozens of sophisticated family offices — including our own. We don't bring a solution to clients until we understand their near-term objectives, long-term goals, and their values."),
        I('hero.photo_url', 'Hero image'),
      ] },
      { title: 'Founders & team grid', fields: [
        T('founders.label', 'Founders section label', 'Founders'),
        T('founders.view_bio', 'Founder card link', 'VIEW FULL BIO'),
        T('grid.title', 'Team grid heading', 'Senior *investment* and *advisory* professionals.'),
        T('grid.hover', 'Headshot hover text', 'VIEW BIO →'),
        T('modal.experience', 'Bio popup: experience label', 'EXPERIENCE'),
        T('modal.founder', 'Bio popup: founder label', 'FOUNDER'),
        T('modal.investment', 'Bio popup: investment label', 'INVESTMENT'),
        T('modal.advisory', 'Bio popup: advisory label', 'ADVISORY'),
      ] },
      { title: 'What earns us the right', fields: [
        T('earns.kicker', 'Kicker', 'WHAT EARNS US THE RIGHT'),
        T('earns.title', 'Heading', 'Experience that *earns the right* to do this work.'),
        L('earns.items', 'Pillars', [f('title', 'Title'), f('desc', 'Description', 'textarea')], pillars),
      ] },
    ],
  },
  advisory: {
    label: 'Advisory Services',
    sections: [
      { title: 'Hero', fields: [
        T('hero.kicker', 'Kicker', 'ADVISORY SERVICES'),
        T('hero.title_line1', 'Heading line 1', 'Where every dollar'),
        T('hero.title_line2', 'Heading line 2 (italic)', 'finds its purpose.'),
        A('hero.body1', 'Body paragraph 1', 'Most families are advised in fragments — an investment manager here, an estate attorney there, a philanthropy conversation that happens once a decade if at all. Full Spectrum Advisory treats every financial decision as part of one coherent strategy.'),
        A('hero.body2', 'Body paragraph 2', 'We act as your quarterback, not just your investment manager — coordinating the specialists in your life so every relationship works toward the same plan.'),
        I('hero.photo_url', 'Hero image'),
      ] },
      { title: 'Full Spectrum Advisory', fields: [
        T('fs.kicker', 'Kicker', 'FULL SPECTRUM ADVISORY'),
        T('fs.title_line1', 'Heading line 1', 'Every dollar has a job.'),
        T('fs.title_line2', 'Heading line 2 (italic)', 'Most plans never ask what it is.'),
        A('fs.body', 'Intro paragraph', "Full Spectrum Advisory is STP's integrated approach to wealth management — one that treats every financial decision as part of a cohesive, coherent strategy for your family's goals."),
        L('fs.blocks', 'Blocks', [f('title', 'Title'), f('desc', 'Description', 'textarea')], [
          { title: 'The Gap', desc: 'Most families are served in silos. Investments here, estate plan there, taxes elsewhere. No single view.' },
          { title: 'The Approach', desc: 'Six interconnected domains of wealth, each understood, scored, and actively managed as part of one integrated plan.' },
          { title: 'The Output', desc: 'Purpose-built portfolios optimized for the best risk-adjusted returns — leaving no stone unturned.' },
        ]),
      ] },
      { title: 'The Six Domains', fields: [
        T('domains.kicker', 'Kicker', 'THE SIX DOMAINS'),
        T('domains.title', 'Heading', 'Every dimension of wealth, mapped and managed as one.'),
        T('domains.hint', 'Card hint', 'Hover to explore'),
        L('domains.items', 'Domains', [f('title', 'Title'), f('desc', 'Description', 'textarea'), f('output', 'Output (italic line)', 'textarea')], sixDomains.map(({ title, desc, output }) => ({ title, desc, output }))),
      ] },
      { title: 'Our Process', fields: [
        T('process.kicker', 'Kicker', 'OUR PROCESS'),
        T('process.title', 'Heading', 'Five deliberate steps, one integrated plan.'),
        L('process.steps', 'Steps', [f('label', 'Step name'), f('desc', 'Description', 'textarea')], [
          { label: 'Discovery', desc: 'Exploratory conversation around the Six Domains and prioritizing goals. Preliminary wealth assessment.' },
          { label: 'Onboarding', desc: 'Full balance sheet assembly, legal documents, entity structure, and system integration.' },
          { label: 'Analysis', desc: 'Baseline return and risk tolerance. Score the stage and progress. Target portfolio construction.' },
          { label: 'Approval', desc: 'Full Spectrum recommendations reviewed, adjusted, and formalized with the family.' },
          { label: 'Execution', desc: 'Portfolio implementation, ongoing monitoring, and active management as a living plan.' },
        ]),
        A('process.body', 'Closing paragraph', "Every advisory domain moves through the same five phases. For each domain, this guide includes a best-practices page, an illustrative timeline, and a deliverable plan— all scoped to be adapted to each family's engagement."),
      ] },
    ],
  },
  investment: {
    label: 'Investment Solutions',
    sections: [
      { title: 'Hero', fields: [
        T('hero.kicker', 'Kicker', 'INVESTMENT SOLUTIONS'),
        T('hero.title_line1', 'Heading line 1', 'Built the way we invest'),
        T('hero.title_line2', 'Heading line 2 (italic)', 'our own capital.'),
        A('hero.body', 'Body paragraph', "STP organizes investments by what they do for a portfolio, not what they're labeled — growth or income across a liquidity spectrum. That framework produces six strategies across three liquidity tiers, plus tailored SMAs and single-asset SPVs for concentrated positions."),
        I('hero.photo_url', 'Hero image'),
      ] },
      { title: 'Principles', fields: [
        L('principles', 'Principles', [f('title', 'Title'), f('desc', 'Description', 'textarea')], [
          { title: "Sourcing You Can't Replicate", desc: 'Relationships across managers, family offices, and institutions.' },
          { title: 'Clarity Over Complexity', desc: 'A small number of understandable choices instead of an asset-class grid.' },
          { title: 'Better Terms Passed Through', desc: 'Scale and relationships convert into pricing, not margin.' },
          { title: 'We Invest Alongside You', desc: 'STP families and partners hold the same investments offered to clients.' },
        ]),
      ] },
      { title: 'Vehicles', fields: [
        T('vehicles.kicker', 'Kicker', 'VEHICLES'),
        T('vehicles.title', 'Heading', 'Six strategies across three liquidity tiers.'),
        T('vehicles.hint', 'Sub-heading', 'Hover any vehicle to take a closer look.'),
        T('vehicles.cta', 'Card hint', 'HOVER TO EXPLORE →'),
        L('vehicles.items', 'Vehicles', [f('tier', 'Tier'), f('purpose', 'Purpose'), f('name', 'Name'), f('tag', 'Tag'), f('description', 'Description', 'textarea')], VEHICLES),
      ] },
      { title: 'Access', fields: [
        T('access.kicker', 'Kicker', 'ACCESS'),
        T('access.title', 'Heading', 'Three ways to access the platform.'),
        L('access.items', 'Access options', [f('title', 'Title'), f('desc', 'Description', 'textarea')], accessOptions),
        A('disclaimer', 'Legal disclaimer', "Fund descriptions are qualitative and for informational purposes only. This is not an offer or solicitation. Any offering is made only through the fund's confidential private placement memorandum. Past performance is not indicative of future results."),
      ] },
    ],
  },
  contact: {
    label: 'Contact page',
    sections: [
      { title: 'Intro', fields: [
        T('hero.kicker', 'Kicker', 'GET IN TOUCH'),
        T('hero.title_line1', 'Heading line 1', 'Schedule a'),
        T('hero.title_line2', 'Heading line 2 (italic)', 'conversation.'),
        A('hero.body', 'Body paragraph', 'The process begins with a conversation. Full Spectrum Advisory is the foundation we build together — a complete understanding of your family, your goals, and the strategy that connects them.'),
      ] },
      { title: 'Offices', fields: [
        L('offices', 'Offices', [f('office', 'Office name'), f('address', 'Street address'), f('city', 'City / State / ZIP'), f('email', 'Email'), f('phone', 'Phone'), f('contact', 'Contact person')], [
          { office: 'New York', address: '535 Fifth Avenue, Suite 2400', city: 'New York, NY 10017', email: 'thall@stpartners.co', phone: '(917) 837-4617', contact: 'Tim Hall · Founder & CEO' },
          { office: 'Philadelphia', address: '1650 Market Street, Suite 3600', city: 'Philadelphia, PA 19103', email: 'dstaal@stpartners.co', phone: '(215) 555-0114', contact: 'Debbie Staal · Director of Operations' },
          { office: 'San Francisco', address: '505 Montgomery Street, Suite 1100', city: 'San Francisco, CA 94111', email: 'bmorrison@stpartners.co', phone: '(415) 555-0175', contact: 'Blake Morrison · Senior Wealth Advisor' },
        ]),
      ] },
      { title: 'Inquiry form', fields: [
        T('form.title', 'Form heading', 'Send us a note'),
        T('form.name', 'Name label', 'Full name'),
        T('form.email', 'Email label', 'Email'),
        T('form.phone', 'Phone label', 'Phone'),
        T('form.wealth', 'Wealth label', 'APPROXIMATE INVESTABLE WEALTH'),
        T('form.wealth_placeholder', 'Wealth placeholder', 'Select a range'),
        L('form.wealth_options', 'Wealth ranges', [f('label', 'Range')], [
          { label: '$5M – $25M' }, { label: '$25M – $100M' }, { label: '$100M – $500M' }, { label: '$500M+' },
        ]),
        T('form.message', 'Message label', 'HOW CAN WE HELP?'),
        T('form.message_placeholder', 'Message placeholder', 'Tell us about your family and goals...'),
        T('form.submit', 'Submit button', 'SUBMIT INQUIRY'),
        A('form.consent', 'Consent / legal text', 'By submitting, you agree to be contacted by Stone Temple Partners. We treat all inquiries with strict confidentiality.'),
        T('thanks.title', 'Thank-you heading', 'Thank you.'),
        A('thanks.body', 'Thank-you message', 'Your message has been received. A member of our team will reach out within one business day.'),
      ] },
    ],
  },
};

export const getDefault = (page, key) => {
  for (const s of PAGE_SCHEMA[page]?.sections || []) {
    const fld = s.fields.find((x) => x.key === key);
    if (fld) return fld.default;
  }
  return undefined;
};
